import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
// import type { WithImmer } from '../immer.d';
import React, { ReactElement, ReactNode } from 'react';
import { createSmartcatTranslation } from './smartcatTranslation';
import { LocaleType } from './locale';
import { LanguageCode } from './types';
import { LOCALE_RESOURCE_HASH } from './localeResourceHash';
import type { LanguageKeyMap, LocaleResourceMap } from './smartcatTranslation';

export { LanguageCode } from './types';
export type LanguageCodeType = LanguageCode;

declare const __ROBONEO_LOCALE_BASE_URL__: string | undefined;

export interface InitLanguageOptions {
  locales?: LocaleResourceMap;
  localeBaseUrl?: string;
  loadLocale?: (languageCode: LocaleType) => Promise<Partial<LanguageKeyMap>>;
  disableRemoteLocale?: boolean;
}

const remoteLocaleCache = new Map<LocaleType, Partial<LanguageKeyMap>>();
const remoteLocaleRequestCache = new Map<LocaleType, Promise<Partial<LanguageKeyMap>>>();
const LOCALE_CDN_BASE_URL = 'https://roboneo-public.meitudata.com/public/roboneo_web/locales';
const LOCAL_DEV_LOCALE_BASE_URL = '/locales';

/**
 * 判断当前是否是 roboneo-web 本地开发环境，本地开发优先读取 public/locales 下的语言包。
 */
function isRoboneoLocalDevelopment() {
  return typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ENV === 'development';
}

/**
 * 获取 SDK 默认语言包 CDN 地址，宿主和本仓库源码都默认走同一个语言资源 hash 目录。
 */
function getDefaultLocaleBaseUrl() {
  if (typeof __ROBONEO_LOCALE_BASE_URL__ === 'string') {
    return __ROBONEO_LOCALE_BASE_URL__;
  }

  if (isRoboneoLocalDevelopment()) {
    return LOCAL_DEV_LOCALE_BASE_URL;
  }

  return `${LOCALE_CDN_BASE_URL}/${LOCALE_RESOURCE_HASH}`;
}

/**
 * 将外部语言码收敛到当前已有语言包文件名，保证请求路径稳定。
 */
function getLocaleResourceCode(languageCode: LanguageCodeType): LocaleType {
  const normalizedCode = languageCode as string;

  if (languageCode === LanguageCode.zhHans) return LanguageCode.zh;
  if (languageCode === LanguageCode.zhHant) return LanguageCode.en;
  if (languageCode === LanguageCode.kor || normalizedCode === 'ko') return LanguageCode.kor;

  return languageCode;
}

/**
 * 兼容历史 initLanguage(language, locales) 调用，同时支持新的远程语言包参数。
 */
function normalizeInitLanguageOptions(options?: LocaleResourceMap | InitLanguageOptions): InitLanguageOptions {
  if (!options) return {};
  if ('locales' in options || 'localeBaseUrl' in options || 'loadLocale' in options || 'disableRemoteLocale' in options) {
    return options as InitLanguageOptions;
  }

  return { locales: options as LocaleResourceMap };
}

/**
 * 拼接语言包请求地址，避免宿主传入 baseUrl 末尾斜杠时生成双斜杠路径。
 */
function buildLocaleUrl(localeBaseUrl: string, languageCode: LocaleType) {
  return `${localeBaseUrl.replace(/\/$/, '')}/${languageCode}.json`;
}

/**
 * 请求并缓存单个语言包，避免相同语言在多个入口初始化时重复请求。
 */
function loadRemoteLocale(languageCode: LocaleType, options: InitLanguageOptions) {
  const cachedLocale = remoteLocaleCache.get(languageCode);
  if (cachedLocale) return Promise.resolve(cachedLocale);

  const cachedRequest = remoteLocaleRequestCache.get(languageCode);
  if (cachedRequest) return cachedRequest;

  const request = (async () => {
    if (options.loadLocale) {
      return options.loadLocale(languageCode);
    }

    const localeBaseUrl = options.localeBaseUrl || getDefaultLocaleBaseUrl();
    if (!localeBaseUrl || typeof fetch !== 'function') {
      return {};
    }

    const response = await fetch(buildLocaleUrl(localeBaseUrl, languageCode));
    if (!response.ok) {
      throw new Error(`Roboneo locale ${languageCode} load failed: ${response.status}`);
    }

    return response.json() as Promise<Partial<LanguageKeyMap>>;
  })().then((locale) => {
    remoteLocaleCache.set(languageCode, locale);
    remoteLocaleRequestCache.delete(languageCode);
    return locale;
  }).catch((error) => {
    remoteLocaleRequestCache.delete(languageCode);
    throw error;
  });

  remoteLocaleRequestCache.set(languageCode, request);
  return request;
}

/**
 * 合并宿主覆盖和远程语言包，宿主覆盖优先级更高。
 */
function mergeLanguageLocales(
  languageCode: LocaleType,
  remoteLocale: Partial<LanguageKeyMap>,
  hostLocales?: LocaleResourceMap,
): LocaleResourceMap {
  return {
    ...hostLocales,
    [languageCode]: {
      ...remoteLocale,
      ...hostLocales?.[languageCode],
    },
  };
}

const setLanguage = (languageCode: LanguageCodeType, locales?: LocaleResourceMap) => {
  const getReactiveLang = () => {
    if (languageCode === LanguageCode.zhHant) {
      return LanguageCode.en;
    }
    if (languageCode === LanguageCode.zhHans) {
      return LanguageCode.zh;
    }
    return languageCode;
  };
  return {
    languageCode,
    smartcatTranslationText: createSmartcatTranslation({
      getReactiveLang,
      locales,
    }),
  };
};
export interface LanguageStoreType {
  languageStore: {
    languageCode: LanguageCodeType;
    smartcatTranslationText: ReturnType<typeof createSmartcatTranslation>;
  };
}

const languageStoreSlice = () => ({
  languageStore: {
    ...setLanguage(LanguageCode.zhHans),
  },
});

export const useLanguageStore = create<LanguageStoreType>()(immer(languageStoreSlice));

/**
 * 带有占位符的翻译方法，支持传入tsx
 *
 * @export
 * @template T
 * @param {keyof LanguageKeyMap} key
 * @param {T} [params]
 * @return {*}  {T extends Record<string, string> ? string : ReactElement}
 */
export function replaceTemplateWithElements<T extends Record<string, ReactElement | string>>(
  key: keyof LanguageKeyMap,
  params?: T,
): T extends Record<string, string> ? string : ReactElement {
  const template = useLanguageStore.getState().languageStore.smartcatTranslationText((v) => v[key]);
  if (!params) return template as T extends Record<string, string> ? string : ReactElement;

  const children: ReactNode[] = [];
  let lastIndex = 0;

  const regex = /\{(\w+)\}/g;
  const matches = Array.from(template.matchAll(regex));

  for (const match of matches) {
    const [placeholder, matchKey] = match;
    const index = match.index ?? 0;

    // 添加 placeholder 之前的文本
    if (index > lastIndex) {
      children.push(template.slice(lastIndex, index));
    }

    // 添加 ReactNode 或字符串
    const value = params[matchKey];
    if (value !== undefined && value !== null) {
      children.push(value);
    } else {
      children.push(''); // 如果没有对应参数就用空字符串
    }

    lastIndex = index + placeholder.length;
  }

  // 添加剩余文本
  if (lastIndex < template.length) {
    children.push(template.slice(lastIndex));
  }
  // 检测是否全是字符串
  const allStrings = children.every((child) => typeof child === 'string');
  if (allStrings) {
    return children.join('') as T extends Record<string, string> ? string : ReactElement;
  }
  return React.createElement(React.Fragment, null, ...children) as any;
}

/**
 * 初始化语言配置
 *
 * @export
 * @param {LanguageCodeType} languageCode
 * @param {LocaleResourceMap | InitLanguageOptions} [options]
 */
export function initLanguage(languageCode: LanguageCodeType, options?: LocaleResourceMap | InitLanguageOptions) {
  const initOptions = normalizeInitLanguageOptions(options);
  const localeResourceCode = getLocaleResourceCode(languageCode);

  useLanguageStore.setState(() => ({
    languageStore: {
      ...setLanguage(languageCode, initOptions.locales),
    },
  }));

  if (initOptions.disableRemoteLocale) {
    return Promise.resolve();
  }

  const hasRemoteSource = Boolean(initOptions.loadLocale || initOptions.localeBaseUrl || getDefaultLocaleBaseUrl());
  if (!hasRemoteSource) {
    return Promise.resolve();
  }

  return loadRemoteLocale(localeResourceCode, initOptions).then((remoteLocale) => {
    useLanguageStore.setState(() => ({
      languageStore: {
        ...setLanguage(languageCode, mergeLanguageLocales(localeResourceCode, remoteLocale, initOptions.locales)),
      },
    }));
  }).catch(() => {
    // 语言包失败时保留当前宿主覆盖或空文案，避免因为 CDN 或宿主 loader 异常阻断业务渲染。
  });
}

/**
 * 获取语言配置
 *
 * @export
 * @return
 */
export function getLanguageSelectConfig(): { value: LanguageCodeType | '' ; label: string; }[] {
  return [
    {
      value: '',
      label: useLanguageStore.getState().languageStore.smartcatTranslationText(
        (v) => v.RoboNeo_Web_toast_setting_follow_system,
      ),
    },
    {
      value: LanguageCode.zhHans,
      label: '简体中文',
    },
    {
      value: LanguageCode.en,
      label: 'English',
    },
    {
      value: LanguageCode.pt,
      label: 'Português(Brasil)',
    },
    {
      value: LanguageCode.ja,
      label: '日本語',
    },
    {
      value: LanguageCode.de,
      label: 'Deutsch',
    },
    {
      value: LanguageCode.kor,
      label: '한국어',
    },
    {
      value: LanguageCode.es,
      label: 'Español(México)',
    },
    {
      value: LanguageCode.th,
      label: 'ไทย',
    },
  ];
}
